<template>
  <div v-if="findings?.length">
    <div class="full-details-wrapper">
      <template v-if="showSubtitle">
        <div class="d-flex align-center px-4 pt-4">
          <div class="subtitle1 mb-4" data-testid="tickets-page-findings-title">
            {{ title }}
          </div>
        </div>
        <v-divider />
      </template>
      <div class="px-4 py-2 findings-wrapper">
        <template v-if="showFileDetails">
          <div class="findings-grid">
            <template v-if="findingsDetails?.filePath">
              <div class="findings-grid__label py-2">{{ $t("ticketDetails.filePath") }}:</div>
              <div class="findings-grid__value py-2">{{ findingsDetails.filePath }}</div>
            </template>
            <template v-if="findingsDetails?.owner">
              <div class="findings-grid__label py-2">{{ $t("ticketDetails.owner") }}:</div>
              <div class="findings-grid__value py-2">{{ findingsDetails.owner }}</div>
            </template>
            <template v-if="lastModified">
              <div class="findings-grid__label py-2">{{ $t("ticketDetails.lastModified") }}:</div>
              <div class="findings-grid__value py-2">{{ lastModified }}</div>
            </template>
          </div>
          <v-divider />
        </template>
        <div class="findings-grid">
          <template v-for="(finding, index) of findingsTemplates" :key="index">
            <div
              class="findings-grid__row"
              :data-testid="`tickets-page-finding-row-${index}`"
              v-html="finding"
            ></div>
            <div v-if="index !== findingsTemplates.length - 1" class="findings-grid__divider" />
          </template>
        </div>
      </div>
    </div>

    <div v-if="additionalFindings?.length" class="full-details-wrapper mt-4">
      <div class="d-flex align-center px-4 pt-4">
        <div class="subtitle1 mb-4" data-testid="tickets-page-findings-title">
          {{ $t("ticketDetails.additionalFindings") }}
        </div>
      </div>
      <v-divider></v-divider>
      <div class="pa-4 findings-wrapper">
        <div class="findings-grid">
          <template v-for="(finding, index) of additionalFindingsTemplates" :key="index">
            <div
              class="findings-grid__row"
              :data-testid="`tickets-page-finding-row-${index}`"
              v-html="finding"
            ></div>
            <div
              v-if="index !== additionalFindingsTemplates.length - 1"
              class="findings-grid__divider"
            />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import type { Finding, TicketDetails } from "@/_store/tickets/tickets.module";
import { convertSize } from "@coronet/cui";
import { createFindingTemplate } from "@/_helpers/ticket-helpers";
import { getFormattedDateTime } from "@/_helpers/date";
import type { FileFinding } from "@/_store/tickets/ticket.module";
import { TicketType } from "@/constants/tickets";

export default defineComponent({
  props: {
    ticket: {
      type: Object as PropType<TicketDetails>,
      required: false,
    },
    showTitle: {
      type: Boolean,
      default: true,
    },
    showSubtitle: {
      type: Boolean,
      default: true,
    },
    findingsDetails: {
      type: Object as PropType<FileFinding>,
      required: false,
    },
  },
  setup(props) {
    const i18n = useI18n();
    const findings = computed(
      () => props.ticket?.sections?.findings || props.findingsDetails?.findings || []
    );

    const additionalFindings = computed(() => props.ticket?.sections?.additionalFindings || []);
    const title = computed(() =>
      i18n.t(
        props.ticket?.eventType === TicketType.BLOCKED_CONNECTION
          ? "ticketDetails.maliciousDomainsIps"
          : "ticketDetails.findings"
      )
    );
    const findingsTemplates = computed(() => getSortedFindingTemplates(findings.value));
    const additionalFindingsTemplates = computed(() =>
      getSortedFindingTemplates(additionalFindings.value)
    );

    // Priority: source > archive filePath > typeUiView > type.
    // source groups findings by origin (e.g. cloud drive); filePath with "#"
    // denotes an entry inside an archive (zip#inner/path) and must sort together;
    // typeUiView is a human-readable label that takes precedence over the raw type enum.
    const getSortedFindingTemplates = (findings: Finding[]) =>
      findings
        .slice()
        .sort((a, b) => {
          if (a.source) {
            return a.source.localeCompare(b.source ?? "");
          }
          // "#" in filePath marks an archive entry (e.g. archive.zip#inner/file)
          if (a.filePath?.includes("#")) {
            return a.filePath.localeCompare(b.filePath ?? "");
          }
          if (a.typeUiView) {
            return a.typeUiView.localeCompare(b.typeUiView ?? "");
          }
          return (a.type ?? "").localeCompare(b.type ?? "");
        })
        .map((finding) => createFindingTemplate(finding, props.ticket!));

    const lastModified = computed(() => {
      const details = props.findingsDetails;
      if (!details?.lastModifiedTime) return "";
      const time = getFormattedDateTime(details.lastModifiedTime);
      return details.lastModifiedBy
        ? i18n.t("ticketDetails.lastModifiedValue", { time, modifiedBy: details.lastModifiedBy })
        : time;
    });

    const showFileDetails = computed(
      () => props.findingsDetails?.filePath || props.findingsDetails?.owner || lastModified.value
    );

    return {
      findings,
      convertSize,
      findingsTemplates,
      additionalFindingsTemplates,
      additionalFindings,
      title,
      showFileDetails,
      lastModified,
    };
  },
});
</script>

<style lang="scss" scoped>
.full-details-wrapper {
  border: 1px solid rgb(var(--v-theme-indigo-faint));
}
</style>
